import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useRegisterAssistantNotices } from '@features/assistant/hooks/useRegisterAssistantContext'
import { ToastContainer } from './ToastContainer'

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number // ms, default 5000. 0 = persistent
  actions?: ToastAction[]
  /** Shared notification identity for callers reporting the same failure. */
  dedupeKey?: string
}

export interface ToastAction {
  label: string
  onClick: () => void
}

interface ToastActions {
  addToast: (toast: Omit<Toast, 'id'>) => string
  removeToast: (id: string) => void
}

const MAX_TOASTS = 5
const DEFAULT_DURATION = 5000

const ToastContext = createContext<ToastActions | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const currentToasts = useRef<Toast[]>([])
  // Arvoke AI reads the errors and warnings on screen.
  useRegisterAssistantNotices(
    toasts
      .filter((toast) => toast.type === 'error' || toast.type === 'warning')
      .map((toast) => (toast.message ? `${toast.title}: ${toast.message}` : toast.title))
  )

  const removeToast = useCallback((id: string) => {
    currentToasts.current = currentToasts.current.filter((toast) => toast.id !== id)
    setToasts(currentToasts.current)
  }, [])

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    // Don't merge actionable notifications by their text: their callbacks can
    // belong to different operations. Callers may supply an explicit identity.
    const dedupeKey =
      toast.dedupeKey ??
      (toast.type === 'error' && !toast.actions?.length
        ? JSON.stringify(['error', toast.title, toast.message ?? ''])
        : undefined)
    // A repeat only merges with a matching toast that is still on screen (a
    // retry loop, or the same failure from several queries at once). It
    // replaces that toast, so it reappears with a full timer instead of
    // stacking. Once the toast is gone, the next failure shows again.
    const remaining = dedupeKey
      ? currentToasts.current.filter((item) => item.dedupeKey !== dedupeKey)
      : currentToasts.current
    const id = crypto.randomUUID()
    const duration = toast.duration ?? DEFAULT_DURATION
    currentToasts.current = [...remaining, { ...toast, id, duration, dedupeKey }].slice(-MAX_TOASTS)
    setToasts(currentToasts.current)

    return id
  }, [])

  // Only the container reads the list. The context carries just the actions,
  // which never change, so showing or dismissing a toast doesn't re-render
  // every component that can raise one.
  const actions = useMemo(() => ({ addToast, removeToast }), [addToast, removeToast])

  return (
    <ToastContext.Provider value={actions}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  )
}

/** The toast actions, or null outside a ToastProvider (a grid rendered on its own). */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalToast(): ToastActions | null {
  return useContext(ToastContext)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastActions {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
